// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { ExistingAsset } from '@/api/types';
import {
  RegisterAssemblyRequest,
  registerAssembly,
} from '@/assembly/api/register-assembly';
import { Assembly } from '@/assembly/api/types';
import { RegisterAssemblyParts } from '@/assembly/components/register-assembly-parts';
import { assembliesQueryKey } from '@/assembly/hooks/use-assemblies';
import { assetMetadataSchema } from '@/asset/api/types';
import { MetadataListForm } from '@/asset/components/metadata';
import { TakenSerialLink } from '@/asset/components/taken-serial-link';
import { FormTextField } from '@/components/form-text-field';
import { LocationName } from '@/components/location-name';
import { SupportAlert } from '@/components/support-alert';
import {
  ViewListOutlined,
  ViewModuleOutlined,
} from '@/constants/hardfin-standard-icons';
import { getItem } from '@/items/api/get-item';
import { ItemDetail } from '@/items/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

interface Props {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog is closed.
   */
  onClose: () => void;

  activeAssembly: Assembly | null;
}

/**
 * Form schema for the register assembly
 */
const formSchema = z.object({
  serial: z.string().min(1, {
    message: 'Serial number must be at least 1 character',
  }),
  description: z.string().nullable(),
  partSerials: z.array(
    z.object({ partId: z.string(), serials: z.array(z.string()) })
  ),
  metadata: z.array(assetMetadataSchema),
});

export type RegisterAssemblyFormData = z.infer<typeof formSchema>;

/**
 * Defaults for the form
 */
const defaultValues: RegisterAssemblyFormData = {
  serial: '',
  description: null,
  partSerials: [],
  metadata: [],
};

// Remove the separate metadata form and its complex synchronization
export function RegisterAssemblyDialog(props: Props) {
  // Setup various states
  const queryClient = useQueryClient();
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  const [activeAssemblyId, setActiveAssemblyId] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<'parts' | 'metadata'>('parts');
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [takenSerialAsset, setTakenSerialAsset] =
    useState<ExistingAsset | null>(null);
  const activeItemData = useRef<ItemDetail | null>(null);
  const dialogBody = useRef<HTMLElement>(null);

  // Setup the main form control only
  const methods = useForm<RegisterAssemblyFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const {
    control,
    formState,
    reset,
    handleSubmit,
    watch,
    setError,
    setValue,
    getValues,
  } = methods;

  // Read every render, so react-hook-form subscribes to the serial error.
  const hasSerialError = formState.errors.serial !== undefined;

  // Remove the separate metadata form and its complex sync effect
  // The MetadataListForm will now work directly with the main form's metadata field

  // Setup the close handler
  const handleClose = () => {
    reset(defaultValues);
    setCurrentTab('parts');
    setActiveAssemblyId(null);
    setFormErrorMessage(null);
    setTakenSerialAsset(null);
    props.onClose();
  };

  // Handle context switch for the property on this dialog
  useEffect(() => {
    // Ensure the assembly ID was set before checking anything this component isn't "valid"
    if (props.activeAssembly && props.activeAssembly.id) {
      // Check to see if the assembly id has changed from what the component state references
      if (activeAssemblyId != props.activeAssembly.id) {
        // Set the active assembly id to the changed assembly id
        setActiveAssemblyId(props.activeAssembly.id);

        // Reset specific fields, a full reset can race the metadata effect
        setValue('description', null);
        setValue('serial', props.activeAssembly.intendedAssetSerial ?? '');
        setValue(
          'partSerials',
          props.activeAssembly.partsUsed.map((part) => ({
            partId: part.id,
            serials: part.serials ?? [],
          })) ?? []
        );
      }
    }
  }, [activeAssemblyId, props.activeAssembly, setValue]);

  const registerAssemblyMutation = useMutation({
    mutationFn: (data: RegisterAssemblyRequest) =>
      registerAssembly(props.activeAssembly?.key ?? '', data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...assembliesQueryKey],
      });
      handleClose();
    },
    onError: (error) => {
      // Dispatch the AxiosError exactly once. hasUniqueViolationError +
      // handleAPIError would each call transformAndDispatchError,
      // double-capturing to Sentry for 500+ errors. Resolve the
      // apiError up front, then route by errorType.
      if (!(error instanceof AxiosError)) {
        captureException(error);
        setFormErrorMessage(SUPPORT_FALLBACK_ERROR_MESSAGE);
        return;
      }
      const apiError = handleAPIError(error);
      if (apiError.errorType === 'UNIQUE_VIOLATION') {
        // Duplicate serial is all that returns this for the query
        setTakenSerialAsset(apiError.existingAsset ?? null);
        setError('serial', {
          type: 'value',
          message: `Serial "${getValues('serial')}" already exists`,
        });
        return;
      }
      const curated = getUserFacingErrorMessage(apiError);
      if (
        curated === SUPPORT_FALLBACK_ERROR_MESSAGE &&
        !apiError.sentToSentry
      ) {
        captureException(error);
      }
      setFormErrorMessage(curated);
    },
  });

  // This effect is used to sync the active item data for the current assembly.
  // The assembly itself only has a sparse item, however this needs all the item details for metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      // Ensure there is an active assembly and its stale
      if (
        props.activeAssembly &&
        props.activeAssembly.item.id != activeItemData.current?.id
      ) {
        // Set the metadata loading
        setIsLoadingMetadata(true);
        try {
          // Get the product details
          const response = await getItem(props.activeAssembly.item.key);
          // Update the selected item

          const defaultValue = response.item.fields.map((field) => {
            return {
              value: null,
              field,
            };
          });

          // Set the metadata value in the form
          setValue('metadata', defaultValue, { shouldDirty: true });

          activeItemData.current = response.item;
        } catch (error) {
          captureException(error);
          setFormErrorMessage('Error loading metadata, please try again.');
        } finally {
          setIsLoadingMetadata(false);
        }
      } else {
        activeItemData.current = null;
      }
    };

    fetchMetadata();
  }, [props.activeAssembly, setValue, setIsLoadingMetadata]);

  const onSubmit = async (data: RegisterAssemblyFormData) => {
    setFormErrorMessage(null);
    setTakenSerialAsset(null);

    return registerAssemblyMutation.mutate({
      serial: data.serial,
      description: data.description,
      parts: data.partSerials,
      metadata: data.metadata
        .filter((field) => field.value !== null)
        .map((field) => ({
          fieldId: field.field.id,
          value: field.value,
        })),
    });
  };

  if (props.activeAssembly === null) {
    return null;
  }

  const metadata = watch('metadata');
  return (
    <FormProvider {...methods}>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={props.isOpen}
        onClose={handleClose}
        scroll="paper"
      >
        <DialogTitle>Register assembly as asset</DialogTitle>
        <DialogContent dividers ref={dialogBody}>
          <form
            style={{ width: '100%' }}
            id="create-assembly-form"
            onSubmit={handleSubmit((data) => {
              if (dialogBody.current) {
                dialogBody.current.scrollTo(0, 0);
              }
              return onSubmit(data);
            })}
          >
            <Stack spacing={2}>
              {formErrorMessage &&
                (formErrorMessage === SUPPORT_FALLBACK_ERROR_MESSAGE ? (
                  <SupportAlert message={formErrorMessage} />
                ) : (
                  <Alert severity="error">{formErrorMessage}</Alert>
                ))}

              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Assembly details
              </Typography>

              <Paper sx={{ p: 3 }} variant="outlined">
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Assembly ID
                    </Typography>
                    <Typography variant="body1">
                      {props.activeAssembly.internalId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Product
                    </Typography>
                    <Typography variant="body1">
                      {props.activeAssembly.item.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Build date
                    </Typography>
                    <Typography variant="body1">
                      {props.activeAssembly.buildDate.toLocaleString({
                        dateStyle: 'medium',
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <LocationName
                      location={props.activeAssembly.location}
                      variant="body1"
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Registered asset
              </Typography>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Stack spacing={0.5}>
                      <FormTextField
                        fullWidth
                        name="serial"
                        label="Serial"
                        control={control}
                      />
                      {takenSerialAsset !== null && hasSerialError && (
                        <TakenSerialLink existingAsset={takenSerialAsset} />
                      )}
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextField
                      fullWidth
                      name="description"
                      label="Description"
                      placeholder="Enter asset description"
                      control={control}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={currentTab}
                onChange={(_e, newValue: 'parts' | 'metadata' | null) => {
                  // Null can come in from a "deselection"
                  if (newValue) {
                    setCurrentTab(newValue);
                  }
                }}
              >
                <ToggleButton value="parts">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    paddingX={1}
                  >
                    <ViewModuleOutlined
                      fontSize="small"
                      sx={{ marginRight: 1 }}
                    />
                    <Typography variant="button" textTransform="none">
                      Parts & serials
                    </Typography>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="metadata" disabled={isLoadingMetadata}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    paddingX={1}
                  >
                    <ViewListOutlined
                      fontSize="small"
                      sx={{ marginRight: 1 }}
                    />
                    <Typography variant="button" textTransform="none">
                      Metadata
                    </Typography>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>

              {currentTab === 'parts' && (
                <RegisterAssemblyParts
                  assembly={props.activeAssembly}
                  control={control}
                />
              )}
              {currentTab === 'metadata' && (
                <MetadataListForm metadata={metadata} />
              )}
            </Stack>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            form="create-assembly-form"
            variant="contained"
            color="primary"
            disabled={registerAssemblyMutation.isPending}
          >
            Register assembly
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
